/**
 * TaskCard Component
 * Individual task display with completion toggle and actions
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  Circle,
  Calendar,
  Banknote,
  Tag,
  MoreVertical,
  Edit2,
  Trash2,
  Clock,
  Link as LinkIcon,
  Archive,
  ArchiveRestore,
  Folder,
  Receipt,
  ShoppingCart,
  Target,
  AlertCircle,
  Paperclip
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { TASK_STATUS, TASK_PRIORITY } from '@/services/finTasksService';
import { goalLinkExplanation } from '@/services/finTasksAIService';
import { getRelativeDueLabel } from '@/utils/dates';
import { CONTEXT_TYPE, getContextPath, getContextColor, hasContextLink, verifyContextLinkExists, getContextTypeName } from '@/utils/contextLinking';
import Tooltip from '@/components/common/Tooltip';

const priorityColors = {
  low: 'text-gray-500',
  medium: 'text-blue-500',
  high: 'text-red-500'
};

const priorityBgColors = {
  low: 'bg-gray-100',
  medium: 'bg-blue-50',
  high: 'bg-red-50'
};

// Tag color mapping for styled display
const TAG_COLORS = {
  // Regular task tags
  'Urgent': '#ef4444',
  'Home': '#10b981',
  'Work': '#3b82f6',
  'Personal': '#8b5cf6',
  'Family': '#f59e0b',
  'Health': '#ec4899',
  'Shopping': '#06b6d4',
  'Errands': '#84cc16',
  // Financial task tags
  'Recurring': '#6366f1',
  'One-time': '#14b8a6',
  'Bill': '#f97316',
  'Savings': '#22c55e',
  'Investment': '#0ea5e9',
  'Tax': '#a855f7',
  'Insurance': '#64748b',
  'Debt': '#ef4444',
};

// Get tag color (returns default gray if not a preset tag)
const getTagColor = (tag) => TAG_COLORS[tag] || '#6b7280';

export default function TaskCard({
  task,
  onToggle,
  onEdit,
  onDelete,
  onArchive,
  onUnarchive,
  onClick,
  onQuickEdit, // Phase 8.7: Quick inline edit callback
  className
}) {
  const navigate = useNavigate();
  const { currency: userCurrency, user } = useAuth();
  const currency = userCurrency || 'USD';
  const [showMenu, setShowMenu] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [goalExplainLoading, setGoalExplainLoading] = useState(false);
  const [goalExplanationText, setGoalExplanationText] = useState(null);
  const [verifyingLink, setVerifyingLink] = useState(false);
  
  // Phase 8.7: Inline edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(task.title);

  const isDone = task.status === TASK_STATUS.DONE;
  const isArchived = task.status === TASK_STATUS.ARCHIVED;
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  
  // Calculate urgency levels based on time until due
  const getUrgencyLevel = () => {
    if (!dueDate || isDone) return 'none';
    
    const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    if (hoursUntilDue < 0) return 'overdue'; // Past due - RED
    if (hoursUntilDue <= 24) return 'urgent'; // Due within 24 hours - AMBER
    if (hoursUntilDue <= 72) return 'soon'; // Due within 3 days - YELLOW/LIGHT
    return 'comfortable'; // More than 3 days - GREEN
  };
  
  const urgencyLevel = getUrgencyLevel();
  const isOverdue = urgencyLevel === 'overdue';

  // Get relative due date label
  const relativeDueInfo = getRelativeDueLabel(task.dueDate, isDone);

  // Determine status label and color
  const getStatusInfo = () => {
    if (isDone) return { label: 'Completed', color: 'text-green-600', bg: 'bg-green-50' };
    if (isOverdue) return { label: 'Overdue', color: 'text-red-600', bg: 'bg-red-50' };
    return { label: 'Pending', color: 'text-blue-600', bg: 'bg-blue-50' };
  };

  const statusInfo = getStatusInfo();

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (isToggling) return;

    setIsToggling(true);
    try {
      await onToggle(task);
    } finally {
      setIsToggling(false);
    }
  };

  // Phase 8.7: Handle inline edit
  const handleStartEdit = (e) => {
    e.stopPropagation();
    if (!onQuickEdit) return; // Only allow if callback provided
    setEditedTitle(task.title);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editedTitle.trim() || editedTitle === task.title) {
      setIsEditing(false);
      setEditedTitle(task.title);
      return;
    }
    
    try {
      await onQuickEdit(task, { title: editedTitle.trim() });
      setIsEditing(false);
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
      setEditedTitle(task.title);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTitle(task.title);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-GB', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  // Urgency-based card styling
  const getUrgencyStyles = () => {
    if (isDone) return ''; // No urgency styling for completed tasks
    
    switch (urgencyLevel) {
      case 'overdue':
        // Past due - Red with red border
        return 'border-red-300 dark:border-red-700 bg-gradient-to-r from-red-50/50 to-red-100/30 dark:from-red-900/20 dark:to-red-900/10 shadow-red-100 dark:shadow-red-900/20';
      case 'urgent':
        // Due within 24 hours - Amber with amber border
        return 'border-amber-300 dark:border-amber-700 bg-gradient-to-r from-amber-50/50 to-orange-50/30 dark:from-amber-900/20 dark:to-orange-900/10 shadow-amber-100 dark:shadow-amber-900/20';
      case 'soon':
        // Due within 3 days - Light yellow tint
        return 'border-yellow-200 dark:border-yellow-800 bg-gradient-to-r from-yellow-50/30 to-amber-50/20 dark:from-yellow-900/10 dark:to-amber-900/10';
      case 'comfortable':
        // More than 3 days - Green/teal border
        return 'border-emerald-200 dark:border-emerald-800 bg-gradient-to-r from-emerald-50/20 to-teal-50/20 dark:from-emerald-900/10 dark:to-teal-900/10';
      default:
        return '';
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      onClick={() => onClick && onClick(task)}
      className={cn(
        'group bg-white dark:bg-slate-800 rounded-xl border-2 p-4 transition-all h-full flex flex-col cursor-pointer overflow-hidden',
        // Base border color (will be overridden by urgency if applicable)
        !dueDate && 'border-gray-200 dark:border-slate-700 hover:border-[#006D77] dark:hover:border-teal-500',
        // Apply urgency styling
        dueDate && getUrgencyStyles(),
        // Hover effect
        dueDate && 'hover:shadow-md dark:hover:shadow-slate-900/30',
        // Completed task opacity
        isDone && 'opacity-60 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800',
        className
      )}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {/* Status Indicator Checkbox */}
        <Tooltip
          label={isDone ? 'Mark this task as incomplete and bring it back to your queue.' : 'Mark this task as done and keep your shopping on track.'}
        >
          <button
            onClick={handleToggle}
            disabled={isToggling}
            className={cn(
              'mt-0.5 flex-shrink-0 transition-all hover:scale-110',
              isToggling && 'opacity-50 cursor-wait'
            )}
            aria-label={isDone ? 'Mark as incomplete' : 'Mark as complete'}
          >
            {isDone ? (
              <CheckCircle2 size={28} className="text-green-600 fill-green-600" strokeWidth={2.5} />
            ) : urgencyLevel === 'overdue' ? (
              <div className="relative">
                <Circle size={28} className="text-red-500 fill-red-100" strokeWidth={2.5} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                </div>
              </div>
            ) : urgencyLevel === 'urgent' ? (
              <div className="relative">
                <Circle size={28} className="text-amber-500 fill-amber-100" strokeWidth={2.5} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
                </div>
              </div>
            ) : urgencyLevel === 'comfortable' ? (
              <Circle size={28} className="text-emerald-400 hover:text-emerald-500" strokeWidth={2} />
            ) : (
              <Circle size={28} className="text-gray-400 hover:text-[#006D77]" strokeWidth={2} />
            )}
          </button>
        </Tooltip>

        {/* Task Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* Phase 8.7: Inline editable title */}
              {isEditing ? (
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onBlur={handleSaveEdit}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  className="w-full text-base font-semibold text-gray-900 dark:text-white bg-white dark:bg-slate-700 border-2 border-[#006D77] dark:border-teal-500 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#006D77]/20 dark:focus:ring-teal-500/20"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <h3
                  onDoubleClick={onQuickEdit ? handleStartEdit : undefined}
                  className={cn(
                    'text-base font-semibold text-gray-900 dark:text-white break-words',
                    isDone && 'line-through text-gray-500 dark:text-gray-400',
                    onQuickEdit && 'cursor-text hover:bg-gray-50 dark:hover:bg-slate-700 rounded px-1 -mx-1 transition-colors'
                  )}
                  title={onQuickEdit ? 'Double-click to edit' : undefined}
                >
                  {task.title}
                </h3>
              )}
              {/* Status Label + Financial Badge */}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className={cn(
                  'inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md',
                  statusInfo.color,
                  statusInfo.bg,
                  'border',
                  isDone && 'border-green-200 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400',
                  isOverdue && 'border-red-200 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400',
                  !isDone && !isOverdue && 'border-blue-200 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                )}>
                  {isDone && <CheckCircle2 size={12} className="text-green-600 dark:text-green-400" />}
                  {isOverdue && <div className="w-2 h-2 bg-red-600 dark:bg-red-400 rounded-full" />}
                  {!isDone && !isOverdue && <Circle size={12} className="text-blue-600 dark:text-blue-400" />}
                  {statusInfo.label}
                </span>
                {/* Financial Badge */}
                {task.isFinancialTask && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700">
                    💰 Financial
                  </span>
                )}
                {/* Envelope Link Badge */}
                {task.linkedEnvelopeId && (
                  <Tooltip label="This task is linked to a budget envelope">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-[#006D77]/10 dark:bg-teal-900/30 text-[#006D77] dark:text-teal-400 border border-[#006D77]/20 dark:border-teal-700">
                      <Banknote size={12} />
                      Budget
                    </span>
                  </Tooltip>
                )}
                {/* Goal Link Badge */}
                {task.goalId && (
                  <Tooltip label="This task is linked to a savings goal">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700">
                      <Target size={12} />
                      Goal
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>

            {/* Menu Button */}
            <div className="relative flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Task options"
              >
                <MoreVertical size={16} className="text-gray-600 dark:text-gray-400" />
              </button>

              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                    }}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    className="absolute right-0 top-8 z-20 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden min-w-[140px]"
                  >
                    <Tooltip label="Edit this task’s details." className="w-full">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          onEdit(task);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                    </Tooltip>
                    
                    {/* Archive/Unarchive button - show for completed or archived tasks */}
                    {(isDone || isArchived) && (
                      <Tooltip
                        label={isArchived ? 'Bring this task back from archive.' : 'Archive this task to tidy your board without deleting it.'}
                        className="w-full"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(false);
                            if (isArchived && onUnarchive) {
                              onUnarchive(task);
                            } else if (isDone && onArchive) {
                              onArchive(task);
                            }
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center gap-2"
                        >
                          {isArchived ? (
                            <>
                              <ArchiveRestore size={14} />
                              Unarchive
                            </>
                          ) : (
                            <>
                              <Archive size={14} />
                              Archive
                            </>
                          )}
                        </button>
                      </Tooltip>
                    )}
                    
                    <Tooltip label="Delete this task. This action cannot be undone." className="w-full">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          onDelete(task);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-2"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </Tooltip>
                  </motion.div>
                </>
              )}
            </div>
          </div>

          {task.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
              {task.description}
            </p>
          )}

          {/* Linked Context - Phase 2: Clickable with type-specific styling & verification */}
          {task.contextLabel && (
            (() => {
              const isClickable = hasContextLink(task);
              const colors = getContextColor(task.contextType || 'none');
              const ContextIcon = task.contextType === CONTEXT_TYPE.BUDGET ? Banknote :
                                 task.contextType === CONTEXT_TYPE.CATEGORY ? Folder :
                                 task.contextType === CONTEXT_TYPE.TRANSACTION ? Receipt :
                                 task.contextType === CONTEXT_TYPE.LIST ? ShoppingCart :
                                 task.contextType === CONTEXT_TYPE.GOAL ? Target :
                                 LinkIcon;

              const handleContextClick = async (e) => {
                e.stopPropagation();
                if (!isClickable || verifyingLink) return;
                
                // Skip verification for categories (they always redirect to budget filter)
                if (task.contextType === CONTEXT_TYPE.CATEGORY) {
                  const path = getContextPath(task.contextType, task.contextId);
                  if (path) navigate(path);
                  return;
                }

                // Verify the linked entity exists before navigating
                setVerifyingLink(true);
                try {
                  const result = await verifyContextLinkExists(
                    task.contextType, 
                    task.contextId, 
                    user?.uid
                  );
                  
                  if (result.exists) {
                    // Use resolved ID if available (e.g., for lists found by name)
                    const resolvedId = result.listId || task.contextId;
                    const path = getContextPath(task.contextType, resolvedId);
                    if (path) navigate(path);
                  } else {
                    // Show friendly message instead of navigating to empty page
                    const typeName = getContextTypeName(task.contextType);
                    toast.error(
                      (t) => (
                        <div className="flex items-start gap-2">
                          <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold">Link unavailable</p>
                            <p className="text-sm text-gray-600">{result.message}</p>
                          </div>
                        </div>
                      ),
                      { duration: 4000 }
                    );
                  }
                } catch (error) {
                  console.error('Error verifying context link:', error);
                  toast.error('Unable to verify link. Please try again.');
                } finally {
                  setVerifyingLink(false);
                }
              };

              return (
                <div
                  onClick={handleContextClick}
                  className={cn(
                    'mt-2 flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border max-w-full transition-all',
                    colors.bg,
                    colors.text,
                    colors.border,
                    isClickable ? `cursor-pointer ${colors.hover}` : 'cursor-default',
                    verifyingLink && 'opacity-50 cursor-wait'
                  )}
                  title={isClickable ? `Click to view ${task.contextType}` : undefined}
                >
                  <ContextIcon size={12} className="shrink-0" />
                  <span className="font-medium shrink-0">Linked to:</span>
                  <span className="font-semibold truncate">{task.contextLabel}</span>
                  {isClickable && !verifyingLink && <span className="ml-1 shrink-0">→</span>}
                  {verifyingLink && <span className="ml-1 animate-pulse shrink-0">...</span>}
                </div>
              );
            })()
          )}

          {/* Meta Info - Improved Hierarchy */}
          <div className="flex items-center justify-between gap-3 mt-3">
            {/* Left: Date + Priority + Relative Due Date */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-xs font-medium">
                {task.dueDate && (
                  <div
                    className={cn(
                      'flex items-center gap-1',
                      isOverdue ? 'text-red-600' : 'text-gray-600'
                    )}
                  >
                    <Calendar size={14} />
                    {formatDate(task.dueDate)}
                  </div>
                )}
                {task.dueDate && task.priority && (
                  <span className="text-gray-400 dark:text-gray-500">•</span>
                )}
                {task.priority && (
                  <span
                    className={cn(
                      'font-semibold',
                      priorityColors[task.priority]
                    )}
                  >
                    {task.priority.toUpperCase()}
                  </span>
                )}
              </div>
              {/* Relative Due Date Label */}
              {relativeDueInfo && (
                <div className={cn('text-xs font-medium', relativeDueInfo.color)}>
                  {relativeDueInfo.text}
                </div>
              )}
            </div>

            {/* Right: Financial Amount */}
            {task.isFinancialTask && task.estimatedAmount && (
              <div className="text-sm font-bold text-[#EF476F] shrink-0">
                {formatCurrency(task.estimatedAmount, currency)}
              </div>
            )}
          </div>

          {/* Tags (Phase 8.6: Show max 3 with overflow indicator) */}
          {task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {task.tags.slice(0, 3).map((tag, index) => {
                const tagColor = getTagColor(tag);
                return (
                  <span
                    key={index}
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: `${tagColor}15`,
                      color: tagColor,
                      border: `1px solid ${tagColor}30`
                    }}
                  >
                    {tag}
                  </span>
                );
              })}
              {task.tags.length > 3 && (
                <Tooltip label={`+${task.tags.length - 3} more: ${task.tags.slice(3).join(', ')}`}>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 cursor-default">
                    +{task.tags.length - 3}
                  </span>
                </Tooltip>
              )}
              {/* Phase 8.8: Attachment Indicator */}
              {task.attachments && task.attachments.length > 0 && (
                <Tooltip label={`${task.attachments.length} attachment${task.attachments.length > 1 ? 's' : ''}`}>
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700">
                    <Paperclip size={10} />
                    {task.attachments.length}
                  </span>
                </Tooltip>
              )}
            </div>
          )}
          
          {/* Attachment indicator when no tags (Phase 8.8) */}
          {(!task.tags || task.tags.length === 0) && task.attachments && task.attachments.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2">
              <Tooltip label={`${task.attachments.length} attachment${task.attachments.length > 1 ? 's' : ''}`}>
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700">
                  <Paperclip size={10} />
                  {task.attachments.length}
                </span>
              </Tooltip>
            </div>
          )}

          {/* Goal Link Explanation (AI) */}
          {task.contextType === 'goal' && (
            <div className="mt-3">
              {!goalExplanationText ? (
                <button
                  type="button"
                  disabled={goalExplainLoading}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (goalExplainLoading) return;
                    setGoalExplainLoading(true);
                    try {
                      console.log('[AI-HOOK] FinTasks', { action: 'goal-link-explain', taskId: task.id, goalId: task.contextId });
                      const data = await goalLinkExplanation({ task: { id: task.id, title: task.title }, goalId: task.contextId });
                      setGoalExplanationText(data.explanation);
                    } catch (err) {
                      console.error('Goal explanation error', err);
                    } finally {
                      setGoalExplainLoading(false);
                    }
                  }}
                  className="text-[11px] font-semibold px-2 py-1 rounded-md bg-[#006D77]/10 dark:bg-teal-900/30 text-[#006D77] dark:text-teal-400 hover:bg-[#006D77]/20 dark:hover:bg-teal-900/50 transition-colors"
                >
                  {goalExplainLoading ? 'Explaining…' : 'How does this help?'}
                </button>
              ) : (
                <p className="text-xs text-gray-600 dark:text-gray-400 italic leading-snug">
                  {goalExplanationText}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
